import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, PlayCircle, MousePointerClick, Compass, Activity, Rocket, RefreshCw, Building2 } from 'lucide-react';

import { createButtonControls } from './ButtonControls';

const GuidedDemo = ({ topoInstance, className = '' }) => {
  const [isDemoStarted, setIsDemoStarted] = useState(false);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [currentSubStepIndex, setCurrentSubStepIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [podOneIsAdded, setPodOneIsAdded] = useState(false);
  const [podTwoIsAdded, setPodTwoIsAdded] = useState(false);
  const [wanIsAdded, setWanIsAdded] = useState(false);
  const [isBackDisabled, setIsBackDisabled] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);

  const { lockButtons, releaseButtons } = createButtonControls(
    setIsBackDisabled, 
    setIsNextDisabled
  );

  const scrollToSection = (id: string) => {
    const [hash, queryString] = id.split('?');

    // Scroll to the section
    if (hash === "#" || hash === "") {
      // Scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    // Update the URL with the query string
    const newUrl = queryString ? `${hash}?${queryString}` : hash;
    window.history.pushState({}, '', newUrl);

    // Manually trigger the `ApplicationForm` to react to the URL change
    window.dispatchEvent(new Event('popstate'));
  };

  const getRandomFloat = (min, max) => {
    // Generate a random float between min and max
    const randomFloat = Math.random() * (max - min) + min;
    // Convert to string with 1 decimal place, then back to number
    return Number(randomFloat.toFixed(2));
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const scenarios = [
    {
      id: 'interact',
      label: 'Explore Network',
      subSteps: [
        {
          label: "Navigate & Zoom",
          detail: "Pan around by dragging or swiping, and zoom in/out with the scroll wheel or pinch gestures.",
          action: () => {
            lockButtons();
            topoInstance.topology.setZoom(1, {animate: true});
            topoInstance.topology.resetPanOffset();
            topoInstance.topology.applyLayout();
            setTimeout(() => {
                topoInstance.topology.setZoom(0.7, {animate: true});
                setTimeout(() => {
                  topoInstance.topology.setZoom(1, {animate: true});
                  releaseButtons();
              }, 800);
            }, 1200);
          }
        },
        {
          label: "Drag & Drop Nodes",
          detail: "Grab nodes or groups and drop them anywhere. Nodes automatically maintain connection clarity during movement - watch links reflow in real-time as you reposition.",
          action: () => {
            lockButtons();
            const r1 = topoInstance.topology.getNode('router1');
            setTimeout(() => {
              topoInstance.topology.setNodePosition(r1.id, r1.coord.x - 20, r1.coord.y);
          setTimeout(() => {
              topoInstance.topology.setNodePosition(r1.id, r1.coord.x + 40, r1.coord.y);
              setTimeout(() => {
                topoInstance.topology.setNodePosition(r1.id, r1.coord.x - 20, r1.coord.y);
                releaseButtons();
              }, 500);
            }, 500);
          }, 500);
          }
        },
        {
          label: "Inspect Interfaces",
          detail: "Hover over interface indicators to reveal in-depth port info. Click to expand labels or quickly toggle them all in the visibility menu—no more guesswork in your topology.",
          action: () => {
            if (topoInstance) {
              const node = topoInstance.topology.getNode('router2');
              if (node && node.interfaces['Gi0/0']) {
                node.interfaces['Gi0/0'].expand();
              }
            }
          }
        },
        {
          label: "Share or Export Map",
          detail: "With two clicks, generate PNG, JPEG, or JSON snapshots of your current map. Share your topology insights or store the layout for later.",
          action: () => {
            const exportButton = document.querySelector('button#export[title="Export"]') as HTMLButtonElement;
            if (exportButton) {
              exportButton.style.backgroundColor = 'rgba(255, 0, 0, 0.5)';
              topoInstance.topology.controlPanel.toggleSubMenu('export', exportButton);
            }
          }
        },
      ]
    },
    {
      id: 'health',
      label: 'Monitor Health',
      subSteps: [
        {
          label: "Highlight Issues",
          detail: "Change node, interface, and link states with a few lines of code to reflect real-time errors or warnings. Immediately spot ‘red’ critical links and keep an eye on ‘yellow’ warnings.",
          action: () => {
            if (topoInstance) {
              topoInstance.topology.getNode('router2').setStatus('error');
              topoInstance.topology.getNode('router2').setInterfaceStatus('Gi0/1', 'error');
              topoInstance.topology.getNode('router3').setStatus('warning');
              topoInstance.topology.getNode('router3').setInterfaceStatus('Gi0/0', 'warning');
              topoInstance.topology.getNode('router2').interfaces['Gi0/0'].edge.setStatus('warning')
              topoInstance.topology.getNode('router2').interfaces['Gi0/1'].edge.setStatus('error');
            }
          }
        },
        {
          label: "Visualize Link Load",
          detail: "Display link load data on your network map—topoSphere supports this feature out-of-the-box!",
          action: () => {
            if (topoInstance) {
              topoInstance.topology.getNode('router2').setStatus('');
              topoInstance.topology.getNode('router2').setInterfaceStatus('Gi0/1', '');
              topoInstance.topology.getNode('router3').setStatus('');
              topoInstance.topology.getNode('router3').setInterfaceStatus('Gi0/0', '');
              topoInstance.topology.getNode('router2').interfaces['Gi0/0'].edge.setStatus('')
              topoInstance.topology.getNode('router2').interfaces['Gi0/1'].edge.setStatus('');

              let r1r2_util1 = topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/0');
              let r1r2_util2 = topoInstance.topology.getUtilizationIndicator('router2', 'Gi0/0');
              let r1r3_util1 = topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/1');
              let r1r3_util2 = topoInstance.topology.getUtilizationIndicator('router3', 'Gi0/1');

              if (!r1r2_util1) {
                r1r2_util1 = topoInstance.topology.addUtilizationIndicator(
                  'router1',
                  'Gi0/0',
                  {value: 50, label: '0.51 Gb/s', expand: true}
                )
              }
              if (!r1r2_util2) {
                r1r2_util2 = topoInstance.topology.addUtilizationIndicator(
                  'router2',
                  'Gi0/0',
                  {value: 90, label: '0.93 Gb/s', expand: true}
                )
              }
              if (!r1r3_util1) {
                r1r3_util1 = topoInstance.topology.addUtilizationIndicator(
                  'router1',
                  'Gi0/1',
                  {value: 50, label: '0.42 Gb/s', expand: false}
                )
              }
              if (!r1r3_util2) {
                r1r3_util2 = topoInstance.topology.addUtilizationIndicator(
                  'router3',
                  'Gi0/1',
                  {value: 90, label: '0.46 Gb/s', expand: false}
                )
              }

              // Function to update both utilization indicators
              const updateUtilizations = () => {
                const value1 = getRandomFloat(0.5, 1);
                const value2 = getRandomFloat(0.6, 0.85);
                const value3 = getRandomFloat(0.5, 1);
                const value4 = getRandomFloat(0.6, 0.85);
                
                r1r2_util1.updateUtilization(value1 * 100, `${value1} Gbps`);
                r1r2_util2.updateUtilization(value2 * 100, `${value2} Gbps`);
                r1r3_util1.updateUtilization(value3 * 100, `${value3} Gbps`);
                r1r3_util2.updateUtilization(value4 * 100, `${value4} Gbps`);
              };

              // Start periodic updates every 2 seconds (2000 milliseconds)
              const updateInterval = setInterval(updateUtilizations, 2000);
            }
          }
        },
        {
          label: "Animate Traffic Flows",
          detail: "See live packet animations across your topology to understand traffic patterns at a glance. Perfect for troubleshooting, capacity planning, or simply wowing your users.",
          action: async () => {

            if (topoInstance) {
              topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/0')?.collapse();
              topoInstance.topology.getUtilizationIndicator('router2', 'Gi0/0')?.collapse();

              if (podOneIsAdded && podTwoIsAdded) {
                topoInstance.topology.removePath('interPodPath');
                await topoInstance.topology.addPath({
                  id: 'interPodPath',
                  nodeIds: ['pod1_leaf2', 'pod1_spine1', 'superSpine1', 'pod2_spine2', 'pod2_leaf5'],
                  animated: true,
                  theme: {
                    flowAnimationIcon: 'network.packet',
                    strokeStyle: 'rgba(0, 0, 255, 0.7)',
                  }
                });
              } else {
                await topoInstance.topology.addPath({
                  id: 'path1',
                  nodeIds: ['router1', 'router2', 'router3'],
                  animated: true,
                  theme: {
                    flowAnimationIcon: 'network.packet',
                    strokeStyle: 'rgba(0, 0, 255, 0.7)',
                  }
                });
              }
            }
          }
        },
      ]
    },
    {
      id: 'update',
      label: 'Adapt & Scale',
      subSteps: [
        {
          label: "Update Topology Dynamically",
          detail: "Inject new nodes and links or remove them on-the-fly. topoSphere updates links and repositions nodes automatically, showcasing how easy it is to reflect network changes in real time.",
          action: async () => {
            lockButtons();

            topoInstance.topology.resetPanOffset();

            await delay(300);
            if (!wanIsAdded) {
              await topoInstance.topology.addNode({
                id: 'wan',
                iconName: 'network.cloud',
                label: 'WAN',
                layer: 0,
                coord: { x: 0, y: 100 }
              });
              await topoInstance.topology.addEdge({
                source: 'router1',
                target: 'wan',
                DELETEsourceInterface: 'Gi0/2',
                DELETEtargetInterface: 'Gi0/0',
                sourceInterface: { labelText: 'Gi0/2' },
                targetInterface: { labelText: 'Gi0/0' },
              });
              await topoInstance.topology.setLayout('layered');
              setWanIsAdded(true);
            }
            await delay(800);
            topoInstance.topology.getNode('router1').labelText = 'Router 1';
            topoInstance.topology.getNode('router2').labelText = 'Router 2';
            topoInstance.topology.getNode('router3').labelText = 'Router 3';
            await delay(1000);
            if (!podOneIsAdded) await addPODOne();
            await topoInstance.topology.setLayout('layered');
            await topoInstance.topology.zoomToFit();
            releaseButtons();
          }
        },
        {
          label: "Experiment with Layouts",
          detail: "Switch between layered, force-directed, or horizontal/vertical flows. Let topoSphere’s built-in algorithms handle the heavy lifting for a clear, visually appealing map.",
          action: async () => {
            lockButtons();
            topoInstance.topology.groups.forEach(group => {
              group.expand();
              topoInstance.topology.removeGroup(group.id);
            });
            topoInstance.topology.addGroup({
              id: 'routers',
              name: 'Routers',
              memberNodes: ['router1', 'router2', 'router3']
            });

            await topoInstance.topology.setLayout('forceDirected');
            await delay(1200);
            await topoInstance.topology.setLayout('layered', {orientation: 'horizontal'});
            await delay(700);
            await topoInstance.topology.setLayout('layered', {orientation: 'vertical', order: 'descending'});
            await delay(700);
            await topoInstance.topology.setLayout('layered');

            releaseButtons();
          }
        },
        {
          label: "Group & Collapse Devices",
          detail: "Cluster related devices into groups and collapse them to reduce clutter. The SDK reroutes edges automatically, so your map stays logical and legible.",
          action: async () => {
            lockButtons();

            await delay(500);

            // Rename POD 1 devices for consistency
            topoInstance.topology.nodes.forEach(node => {
              if (
                  node.id && 
                  typeof node.id === 'string' && 
                  node.id.toLowerCase().startsWith('pod1') &&
                  node.labelText && 
                  typeof node.labelText === 'string' && 
                  !node.labelText.startsWith('POD1 ')
              ) {
                  // Append 'POD1 ' to the beginning of the existing label
                  node.labelText = 'POD1 ' + node.labelText;
              }
            });
            
            //topoInstance.topology.setZoom(1);
            
            if (!podTwoIsAdded) {
              await addPODTwo();
            }
            await topoInstance.topology.setLayout('layered');

            topoInstance.topology.zoomToFit();
            topoInstance.topology.removePath('interPodPath');
            addInterPodPath();

            await delay(1000);

            addPodGroups();

            await delay(2000);

            
            topoInstance.topology.getNode('pod2_leaf5').setStatus('normal');
            topoInstance.topology.getGroup('pod1')?.collapse()

            await delay(2000);
            topoInstance.topology.getGroup('pod2')?.collapse();

            await topoInstance.topology.setZoom(0.65);
            await topoInstance.topology.setLayout('layered');

            releaseButtons();
          }
        },
        {
          label: "Combine All Features",
          detail: "Now see how easy it is to orchestrate multiple features in tandem—group collapses, link statuses, real-time expansions—all with minimal code overhead.",
          action: async () => {
            lockButtons();
            await addPODOne();
            await addPODTwo();
            await addPodGroups();
            addInterPodPath();

            topoInstance.topology.getGroup('pod2')?.collapse();
            topoInstance.topology.getNode('router1').setStatus('error');

            await delay(1500);

            topoInstance.topology.getGroup('routers')?.collapse();
            topoInstance.topology.getNode('pod2_leaf5').setStatus('warning');
            
            await delay(700);

            await topoInstance.topology.setZoom(0.45);
            topoInstance.topology.getGroup('pod2')?.expand();

            await delay(1500);

            let r1wan_util1 = topoInstance.topology.getUtilizationIndicator('router1', 'Gi0/2');
            let r1wan_util2 = topoInstance.topology.getUtilizationIndicator('wan', 'Gi0/0');

            if (!r1wan_util1) {
              r1wan_util1 = topoInstance.topology.addUtilizationIndicator(
                'router1',
                'Gi0/2',
                {value: 91, label: '0.91 Gb/s', expand: true}
              )
            }
            if (!r1wan_util2) {
              r1wan_util2 = topoInstance.topology.addUtilizationIndicator(
                'wan',
                'Gi0/0',
                {value: 70, label: '0.70 Gb/s', expand: true}
              )
            }

            const updateUtilizations = () => {
              const value1 = getRandomFloat(0.7, 0.8);
              const value2 = getRandomFloat(0.85, 0.98);
              
              r1wan_util1.updateUtilization(value1 * 100, `${value1} Gbps`);
              r1wan_util2.updateUtilization(value2 * 100, `${value2} Gbps`);
            };

            // Start periodic updates every 2 seconds (2000 milliseconds)
            setInterval(updateUtilizations, 2000);

            releaseButtons();
          }
        },
      ]
    },
  ];


  const addPODOne = async () => {
    if (podOneIsAdded) return;
    setPodOneIsAdded(true);
    // ADD LEAF-SPINE topology
    // Border Leaf Switches (Layer 2)
    // These switches connect the external routers to the spine layer
    await topoInstance.topology.addNode({
      id: 'pod1_borderLeaf1',
      iconName: 'network.switch',
      label: 'Border Leaf 1',
      layer: 300,
      coord: { x: -137.66666666666669, y: -131.38255122273628 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_borderLeaf2',
      iconName: 'network.switch',
      label: 'Border Leaf 2',
      layer: 300,
      coord: { x: 137.66666666666669, y: -131.38255122273628 }
    });

    // Spine Switches (Layer 3)
    // Core backbone of the network, providing redundant paths
    await topoInstance.topology.addNode({
      id: 'pod1_spine1',
      iconName: 'network.switch',
      label: 'Spine 1',
      layer: 301,
      coord: { x: -137.66666666666669, y: 32.2058162590879 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_spine2',
      iconName: 'network.switch',
      label: 'Spine 2',
      layer: 301,
      coord: { x: 137.66666666666669, y: 32.2058162590879 }
    });

    // Leaf Switches (Layer 4)
    // Access layer switches that connect to end devices
    await topoInstance.topology.addNode({
      id: 'pod1_leaf1',
      iconName: 'network.switch',
      label: 'Leaf 1',
      layer: 302,
      coord: { x: -285, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf2',
      iconName: 'network.switch',
      label: 'Leaf 2',
      layer: 302,
      coord: { x: -171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf3',
      iconName: 'network.switch',
      label: 'Leaf 3',
      layer: 302,
      coord: { x: -57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf4',
      iconName: 'network.switch',
      label: 'Leaf 4',
      layer: 302,
      coord: { x: 57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf5',
      iconName: 'network.switch',
      label: 'Leaf 5',
      layer: 302,
      coord: { x: 171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod1_leaf6',
      iconName: 'network.switch',
      label: 'Leaf 6',
      layer: 302,
      coord: { x: 285, y: 195.79418374091208 }
    });

    // Router to Border Leaf Connections
    // Connecting external routers to the fabric
    await topoInstance.topology.addEdge({
      source: 'router2',
      target: 'pod1_borderLeaf1',
      DELETEsourceInterface: 'Gi0/3',
      DELETEtargetInterface: 'Eth0',
      sourceInterface: { labelText: 'Gi0/3', visible: true },
      targetInterface: { labelText: 'Eth0', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'router2',
      target: 'pod1_borderLeaf2',
      DELETEsourceInterface: 'Gi0/4',
      DELETEtargetInterface: 'Eth47',
      sourceInterface: { labelText: 'Gi0/4', visible: true },
      targetInterface: { labelText: 'Eth47', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'router3',
      target: 'pod1_borderLeaf1',
      DELETEsourceInterface: 'Gi0/3',
      DELETEtargetInterface: 'Eth48',
      sourceInterface: { labelText: 'Gi0/3', visible: true },
      targetInterface: { labelText: 'Eth48', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'router3',
      target: 'pod1_borderLeaf2',
      DELETEsourceInterface: 'Gi0/4',
      DELETEtargetInterface: 'Eth46',  // Changed from Eth47 to avoid duplicate
      sourceInterface: { labelText: 'Gi0/4', visible: true },
      targetInterface: { labelText: 'Eth46', visible: true }
    });

    // Border Leaf to Spine Connections
    // Creating redundant paths through the spine layer
    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf1',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'Eth1',  // Changed from Eth0 to avoid duplicate
      DELETEtargetInterface: 'eth0',
      sourceInterface: { labelText: 'Eth1', visible: true },
      targetInterface: { labelText: 'eth0', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf1',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth4',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth4', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf2',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth7',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth7', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_borderLeaf2',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth1',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // Spine to Leaf Connections - Spine 1
    // Connecting first spine switch to all leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf1',
      DELETEsourceInterface: 'eth1',  // Changed from eth0 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf2',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf3',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf4',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf5',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine1',
      target: 'pod1_leaf6',
      DELETEsourceInterface: 'eth6',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // Spine to Leaf Connections - Spine 2
    // Connecting second spine switch to all leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf2',
      DELETEsourceInterface: 'eth6',  // Changed from eth1 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf3',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf4',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf5',
      DELETEsourceInterface: 'eth7',  // Changed from eth4 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth7', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod1_spine2',
      target: 'pod1_leaf6',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });            

  }

  const addPODTwo = async () => {
    if (podTwoIsAdded) return;
    setPodTwoIsAdded(true);
    // Super-Spine Switches (Layer 2)
    await topoInstance.topology.addNode({
      id: 'superSpine1',
      iconName: 'network.switch',
      label: 'Super-Spine 1',
      layer: 300,
      coord: { x: -137.66666666666669, y: -131.38255122273628 }
    });

    await topoInstance.topology.addNode({
      id: 'superSpine2',
      iconName: 'network.switch',
      label: 'Super-Spine 2',
      layer: 300,
      coord: { x: 137.66666666666669, y: -131.38255122273628 }
    });

    // POD 2 Spine Switches (Layer 3)
    // Core backbone of the network, providing redundant paths
    await topoInstance.topology.addNode({
      id: 'pod2_spine1',
      iconName: 'network.switch',
      label: 'POD 2 Spine 1',
      layer: 301,
      coord: { x: -137.66666666666669, y: 32.2058162590879 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_spine2',
      iconName: 'network.switch',
      label: 'POD 2 Spine 2',
      layer: 301,
      coord: { x: 137.66666666666669, y: 32.2058162590879 }
    });

    // POD2 Leaf Switches (Layer 4)
    // Access layer switches that connect to end devices
    await topoInstance.topology.addNode({
      id: 'pod2_leaf1',
      iconName: 'network.switch',
      label: 'POD2 Leaf 1',
      layer: 302,
      coord: { x: -285, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf2',
      iconName: 'network.switch',
      label: 'POD2 Leaf 2',
      layer: 302,
      coord: { x: -171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf3',
      iconName: 'network.switch',
      label: 'POD2 Leaf 3',
      layer: 302,
      coord: { x: -57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf4',
      iconName: 'network.switch',
      label: 'POD2 Leaf 4',
      layer: 302,
      coord: { x: 57, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf5',
      iconName: 'network.switch',
      label: 'POD2 Leaf 5',
      layer: 302,
      coord: { x: 171, y: 195.79418374091208 }
    });

    await topoInstance.topology.addNode({
      id: 'pod2_leaf6',
      iconName: 'network.switch',
      label: 'POD2 Leaf 6',
      layer: 302,
      coord: { x: 285, y: 195.79418374091208 }
    });

    // Super-Spine to POD 2 Spine Connections
    // Creating redundant paths through the pod2_spine layer
    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod2_spine1',
      DELETEsourceInterface: 'Eth1', 
      DELETEtargetInterface: 'eth0',
      sourceInterface: { labelText: 'Eth1', visible: true },
      targetInterface: { labelText: 'eth0', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod2_spine2',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth4',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth4', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod2_spine1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth7',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth7', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod2_spine2',
      DELETEsourceInterface: 'eth1',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // Super-Spine to POD 1 Spine Connections
    // Creating redundant paths through the pod1_spine layer
    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'eth22', 
      DELETEtargetInterface: 'eth28',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth28', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine1',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth22',
      DELETEtargetInterface: 'eth28',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth28', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod1_spine1',
      DELETEsourceInterface: 'eth22',
      DELETEtargetInterface: 'eth27',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth27', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'superSpine2',
      target: 'pod1_spine2',
      DELETEsourceInterface: 'eth22',
      DELETEtargetInterface: 'eth27',
      sourceInterface: { labelText: 'eth22', visible: true },
      targetInterface: { labelText: 'eth27', visible: true }
    });

    // POD 2 Spine to POD2 Leaf Connections - POD 2 Spine 1
    // Connecting first pod2_spine switch to all pod2_leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf1',
      DELETEsourceInterface: 'eth1',  // Changed from eth0 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth1', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf2',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf3',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf4',
      DELETEsourceInterface: 'eth4',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth4', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf5',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine1',
      target: 'pod2_leaf6',
      DELETEsourceInterface: 'eth6',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    // POD 2 Spine to POD2 Leaf Connections - POD 2 Spine 2
    // Connecting second pod2_spine switch to all pod2_leaf switches
    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf1',
      DELETEsourceInterface: 'eth0',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth0', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf2',
      DELETEsourceInterface: 'eth6',  // Changed from eth1 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth6', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf3',
      DELETEsourceInterface: 'eth2',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth2', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf4',
      DELETEsourceInterface: 'eth3',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth3', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf5',
      DELETEsourceInterface: 'eth7',  // Changed from eth4 to avoid duplicate
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth7', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });

    await topoInstance.topology.addEdge({
      source: 'pod2_spine2',
      target: 'pod2_leaf6',
      DELETEsourceInterface: 'eth5',
      DELETEtargetInterface: 'eth1',
      sourceInterface: { labelText: 'eth5', visible: true },
      targetInterface: { labelText: 'eth1', visible: true }
    });
  }

  const addPodGroups = async () => {
    if (!topoInstance.topology.getGroup('pod1')) {
      // Create POD1 group - includes border leafs, spines, and leafs
      topoInstance.topology.addGroup({
        id: 'pod1',
        name: 'POD 1',
        memberNodes: [
          //'pod1_borderLeaf1', 'pod1_borderLeaf2',
          'pod1_spine1', 'pod1_spine2',
          'pod1_leaf1', 'pod1_leaf2', 'pod1_leaf3', 
          'pod1_leaf4', 'pod1_leaf5', 'pod1_leaf6'
        ]
      });
    }
    if (!topoInstance.topology.getGroup('pod2')) {
      // Create POD2 group - includes spines and leafs
      topoInstance.topology.addGroup({
        id: 'pod2',
        name: 'POD 2',
        memberNodes: [
          'pod2_spine1', 'pod2_spine2',
          'pod2_leaf1', 'pod2_leaf2', 'pod2_leaf3', 
          'pod2_leaf4', 'pod2_leaf5', 'pod2_leaf6'
        ]
      });
    }
  }

  const addInterPodPath = () => {
    if (topoInstance.topology.getPath('interPodPath')) return;
    topoInstance.topology.addPath({
      id: 'interPodPath',
      nodeIds: ['pod1_leaf2', 'pod1_spine1', 'superSpine2', 'pod2_spine2', 'pod2_leaf5'],
      animated: true,
      theme: {
        flowAnimationIcon: 'network.packet',
        strokeStyle: 'rgba(0, 0, 255, 0.7)',
      }
    });
  }

  const handleSubStepChange = async (subStepIndex) => {
    setCurrentSubStepIndex(subStepIndex);
    const scenario = scenarios[currentScenarioIndex];
    const subStep = scenario.subSteps[subStepIndex];
    if (subStep.action) {
      await subStep.action();
    }
  };

  const handleScenarioChange = async (scenarioId) => {
    try {
      if (topoInstance) {
        const topology = topoInstance.topology;
        try {
          await topology.removePath('path1');
          await topology.removePath('interPodPath');
          topoInstance.topology.getNode('pod2_leaf5')?.setStatus('normal');
          topoInstance.topology.getNode('router1')?.setStatus('normal');
          topology.removeNode('wan');
          setWanIsAdded(false);
          topology.setLayout('layered', {adjustZoom: false});
          topology.groups.map(group => group.id).forEach(groupId => {
            topology.removeGroup(groupId);
          })
          
          topology.addGroup({
            id: 'routers',
            name: 'Routers',
            memberNodes: ['router1', 'router2', 'router3']
          });
        } catch (e) {}
        
        ['router1', 'router2', 'router3'].forEach(nodeId => {
          const node = topology.getNode(nodeId);
          if (node) {
            node.setStatus('normal');
            Object.values(node.interfaces).forEach(iface => {
              if (iface.edge) {
                iface.edge.setStatus('normal');
                iface.setStatus('normal');
                iface.collapse();
              }
            });
          }
        });
      }
    } catch (error) {
      console.error('Error executing scenario:', error);
    }
  };

  const handleNext = async () => {
    const currentScenario = scenarios[currentScenarioIndex];
    if (currentSubStepIndex < currentScenario.subSteps.length - 1) {
      // Within the same scenario, just move to next substep
      handleSubStepChange(currentSubStepIndex + 1);
    } else if (currentScenarioIndex < scenarios.length - 1) {
      // Moving to a new scenario
      const nextIndex = currentScenarioIndex + 1;
      setCurrentScenarioIndex(nextIndex);
      setCurrentSubStepIndex(0);
      // First handle the scenario change to reset the state
      await handleScenarioChange(scenarios[nextIndex].id);
      // Then explicitly trigger the first substep's action
      const nextScenario = scenarios[nextIndex];
      const firstSubStep = nextScenario.subSteps[0];
      if (firstSubStep.action) {
        await firstSubStep.action();
      }
    } else {
      setIsCompleted(true);
    }
    topoInstance.topology.resetPanOffset();
  }

  const handleBack = () => {
    if (isCompleted) {
      setIsCompleted(false);
      setCurrentScenarioIndex(0);
      setCurrentSubStepIndex(0);
      handleScenarioChange(scenarios[0].id);
      return;
    }
    if (currentSubStepIndex > 0) {
      handleSubStepChange(currentSubStepIndex - 1);
    } else if (currentScenarioIndex > 0) {
      const prevIndex = currentScenarioIndex - 1;
      setCurrentScenarioIndex(prevIndex);
      setCurrentSubStepIndex(scenarios[prevIndex].subSteps.length - 1);
      handleScenarioChange(scenarios[prevIndex].id);
    }
    topoInstance.topology.resetPanOffset();
  };

  const handleStart = () => {
    setIsDemoStarted(true);
    topoInstance.topology.controlPanel.show();
    handleScenarioChange(scenarios[0].id);
    const firstSubStep = scenarios[0].subSteps[0];
    if (firstSubStep.action) {
      firstSubStep.action();
    }
    window.addEventListener('topoSphere.groupExpanded', () => {
      setTimeout(() => topoInstance.topology.setLayout('layered'), 200);
    });
    window.addEventListener('topoSphere.groupCollapsed', () => {
      setTimeout(() => topoInstance.topology.setLayout('layered'), 200);
    });
  };

  const generatePOD = (config) => {
    const { podId, layers, counts } = config;
    const nodes = [];
    const edges = [];
  
    // Helper function to generate a unique interface name
    const generateInterfaceName = (index) => `eth${index}`;
  
    // 1. Generate Super-Spine Switches
    for (let i = 0; i < counts.superSpine; i++) {
      nodes.push({
        id: `${podId}_superSpine${i + 1}`,
        iconName: 'network.switch',
        label: `${podId.toUpperCase()} Super-Spine ${i + 1}`,
        layer: layers.superSpineLayer
      });
    }
  
    // 2. Generate Spine Switches
    for (let i = 0; i < counts.spine; i++) {
      nodes.push({
        id: `${podId}_spine${i + 1}`,
        iconName: 'network.switch',
        label: `${podId.toUpperCase()} Spine ${i + 1}`,
        layer: layers.spineLayer
      });
    }
  
    // 3. Generate Leaf Switches
    for (let i = 0; i < counts.leaf; i++) {
      nodes.push({
        id: `${podId}_leaf${i + 1}`,
        iconName: 'network.switch',
        label: `${podId.toUpperCase()} Leaf ${i + 1}`,
        layer: layers.leafLayer
      });
    }
  
    // 4. Generate Border Leaf Switches
    for (let i = 0; i < counts.borderLeaf; i++) {
      nodes.push({
        id: `${podId}_borderLeaf${i + 1}`,
        iconName: 'network.switch',
        label: `${podId.toUpperCase()} Border Leaf ${i + 1}`,
        layer: layers.borderLeafLayer
      });
    }
  
    // 5. Generate Super-Spine to Spine connections
    let interfaceCounter = 0;
    for (let i = 0; i < counts.superSpine; i++) {
      for (let j = 0; j < counts.spine; j++) {
        edges.push({
          source: `${podId}_superSpine${i + 1}`,
          target: `${podId}_spine${j + 1}`,
          DELETEsourceInterface: generateInterfaceName(interfaceCounter),
          DELETEtargetInterface: generateInterfaceName(interfaceCounter + 1),
          sourceInterface: { labelText: generateInterfaceName(interfaceCounter), visible: true },
          targetInterface: { labelText: generateInterfaceName(interfaceCounter + 1), visible: true }
        });
        interfaceCounter += 2;
      }
    }
  
    // 6. Generate Spine to Leaf connections
    for (let i = 0; i < counts.spine; i++) {
      for (let j = 0; j < counts.leaf; j++) {
        edges.push({
          source: `${podId}_spine${i + 1}`,
          target: `${podId}_leaf${j + 1}`,
          DELETEsourceInterface: generateInterfaceName(interfaceCounter),
          DELETEtargetInterface: generateInterfaceName(interfaceCounter + 1),
          sourceInterface: { labelText: generateInterfaceName(interfaceCounter), visible: true },
          targetInterface: { labelText: generateInterfaceName(interfaceCounter + 1), visible: true }
        });
        interfaceCounter += 2;
      }
    }
  
    // 7. Generate Spine to Border Leaf connections
    for (let i = 0; i < counts.spine; i++) {
      for (let j = 0; j < counts.borderLeaf; j++) {
        edges.push({
          source: `${podId}_spine${i + 1}`,
          target: `${podId}_borderLeaf${j + 1}`,
          DELETEsourceInterface: generateInterfaceName(interfaceCounter),
          DELETEtargetInterface: generateInterfaceName(interfaceCounter + 1),
          sourceInterface: { labelText: generateInterfaceName(interfaceCounter), visible: true },
          targetInterface: { labelText: generateInterfaceName(interfaceCounter + 1), visible: true }
        });
        interfaceCounter += 2;
      }
    }
  
    return { nodes, edges };
  }

  if (!isDemoStarted) {
    return (
      <div className="w-full h-full flex flex-col bg-[#0a1425] divide-y divide-white/10">
        {/* Main content area */}
        <div className="flex-1 p-5">
          {/* Header section */}
          <div className="mb-5">
            <h3 className="text-lg font-medium text-white/90 mb-2">
              Beyond Diagrams
            </h3>
            <p className="text-sm text-white/70 leading-relaxed">
              Step into an interactive environment that transforms complex network management into intuitive visual workflows.
            </p>
          </div>
  
          {/* Feature highlights */}
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <MousePointerClick className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-white/90 mb-0.5">Live Environment</div>
                <div className="text-xs text-white/70 leading-relaxed">
                  Experience the real, fully interactive demo of topoSphere. What you see is what you get.
                </div>
              </div>
            </div>
  
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Compass className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-white/90 mb-0.5">Guided Tour</div>
                <div className="text-xs text-white/70 leading-relaxed">
                  Learn by doing. A quick hands-on walkthrough that shows, not tells.
                </div>
              </div>
            </div>
  
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Activity className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-white/90 mb-0.5">Real Scenarios</div>
                <div className="text-xs text-white/70 leading-relaxed">
                  Explore practical, low-code-driven use cases designed for real-world networks
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* Action button */}
        <div className="p-4">
          <button
            onClick={handleStart}
            className="w-full py-2.5 bg-blue-500 text-white rounded-lg
                     hover:bg-blue-600 transition-colors duration-200
                     flex items-center justify-center gap-2 font-medium text-sm"
          >
            <PlayCircle className="w-4 h-4" />
            Start Interactive Demo
          </button>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 p-6 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-green-500">
              <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h4 className="text-lg font-medium text-white mb-6">
            Enjoyed the demo? Share your feedback and be the first to know!
          </h4>
          <div className="space-y-4 w-full max-w-sm">
            <a
              href="#waitlist"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#waitlist');
              }}
              className="block w-full bg-gradient-to-r from-[#d35400] to-[#e67e22] 
                         text-white px-4 py-2.5 rounded-lg text-sm font-semibold 
                         hover:from-[#b24300] hover:to-[#c57c1f] transition-all shadow-lg
                         flex items-center justify-center gap-2"
            >
              <Rocket className="w-4 h-4" /> {/* Icon for "Join Early Access" */}
              Join the Waitlist
            </a>
            <button
              onClick={handleBack}
              className="w-full px-4 py-2 border border-white/20 text-white/90 rounded-lg 
                       hover:bg-white/10 transition-colors text-sm
                       flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> {/* Icon for "Review Demo Again" */}
              Review Demo Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentScenario = scenarios[currentScenarioIndex];
  const currentSubStep = currentScenario.subSteps[currentSubStepIndex];

  return (
    <div className="w-full h-full flex flex-col">
      <div className="bg-[#0a1425] border-b border-white/10">
        <div className="px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-white/60">
            Step <span className="font-semibold text-white/70">{currentScenarioIndex + 1}.{currentSubStepIndex + 1}</span> of {scenarios.length}
          </span>
          <span className="text-sm font-medium text-white/90">{currentScenario.label}</span>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Vertical Tabs and Instructions */}
        <div className="w-full bg-[#0a1425] border-r border-white/10 flex flex-col">
          {/* Step guidance separator */}
          <div className="px-3 py-1.5 pb-5 border-b border-white/10 bg-black/15">
            <p className="text-xs text-white/40 italic">Use Next or jump to any step</p>
          </div>
          {/* Tabs in upper portion */}
          <div className="overflow-y-auto flex-shrink-0">
            {currentScenario.subSteps.map((subStep, index) => (
              <button
                key={index}
                onClick={() => handleSubStepChange(index)}
                className={`
                  w-full px-4 py-2.5 text-sm font-medium text-left transition-all
                  ${index === currentSubStepIndex
                    ? 'bg-[#050614] text-blue-400 border-l-2 border-l-blue-500'
                    : 'text-white/60 hover:text-white/90 hover:bg-white/5 border-l-2 border-l-transparent'
                  }
                `}
              >
                {subStep.label}
              </button>
            ))}
          </div>

          {/* Instructions in lower portion */}
          <div className="flex-1 border-t border-white/10 p-4">
            <p className="text-white/70 text-sm">{currentSubStep.detail}</p>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 bg-[#050614]">
          {/* Topology visualization will be rendered here by the parent component */}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="p-3 border-t border-white/10">
        <div className="flex justify-between gap-2">
        <button
            onClick={handleBack}
            disabled={isBackDisabled || (currentScenarioIndex === 0 && currentSubStepIndex === 0)}
            className="px-4 py-2 rounded-lg border border-white/20 text-white/90 
                    disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 
                    transition-colors flex-1 text-sm flex items-center justify-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
        </button>
        <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white 
                    hover:bg-blue-600 transition-colors flex-1 text-sm
                    disabled:opacity-50 disabled:cursor-not-allowed
                    flex items-center justify-center gap-1"
          >
            Next
            <ChevronRight className="w-4 h-4" />
        </button>
        </div>
      </div>
    </div>
  );
};

export default GuidedDemo;